import {
  CharterAdministratorDto,
  CharterClauseCategory,
  CharterClauseDto,
  CharterGovernanceDto,
  CharterSigningRuleDto,
  CharterSigningScope,
  CharterValueDto,
} from '@interfaces/corporate-charter/responses/corporate-charter.response';
import {
  GOVERNANCE_BODY_LABEL,
  formatMoney,
  formatPercent,
} from '@features/dashboard/company/corporate-charter/corporate-charter.util';
import { CharterProvenance } from '@features/dashboard/company/corporate-charter/corporate-charter-provenance.util';

/**
 * "Quem assina pela empresa" (PBI 3260): traduz as regras de assinatura lidas do contrato (`signingRules`, já
 * classificadas por `scope` pelo agente) em respostas práticas por situação: a quem recorrer para assinar um contrato
 * com banco, dar uma garantia, vender ou onerar um bem, os atos do dia a dia e as procurações.
 *
 * Só usa o que o DTO traz. Quando o contrato não fala de uma situação, a tela diz que ele não especifica e aponta a
 * regra geral; nunca completa a regra por conta própria.
 */

export type CharterSigningSituationKey = 'banking' | 'guarantees' | 'assets' | 'general';

/** Situações na ordem da tela e os `scope` das regras que respondem a cada uma. */
const SITUATIONS: {
  key: CharterSigningSituationKey;
  title: string;
  icon: string;
  scopes: CharterSigningScope[];
  clauses: CharterClauseCategory[];
}[] = [
  {
    key: 'banking',
    title: 'Contratos com bancos, empréstimos e financiamentos',
    icon: 'pi-building-columns',
    scopes: ['financial-obligations', 'banking'],
    clauses: ['debt-restriction'],
  },
  {
    key: 'guarantees',
    title: 'Garantias, aval e fiança',
    icon: 'pi-shield',
    scopes: ['guarantees'],
    clauses: ['guarantee-restriction'],
  },
  {
    key: 'assets',
    title: 'Venda ou oneração de bens',
    icon: 'pi-home',
    scopes: ['asset-sale', 'asset-encumbrance'],
    clauses: ['encumbrance-restriction'],
  },
  {
    key: 'general',
    title: 'Atos do dia a dia e representação geral',
    icon: 'pi-briefcase',
    scopes: ['general', 'other'],
    clauses: [],
  },
];

/** Pessoa a acionar: nome e cargo (quando o nome bate com um administrador lido). */
export interface CharterSigningPerson {
  name: string;
  role: string | null;
}

/** Uma regra traduzida: quem e em que combinação. */
export interface CharterSigningLine {
  people: CharterSigningPerson[];
  /** "qualquer um, isoladamente", "2 em conjunto", "precisa de aprovação do conselho". */
  combination: string;
  /** "até R$ 500.000,00" ou "limite citado: R$ 500.000,00". */
  limit: string | null;
  /** Texto da regra como o Cog leu (aparece quando a combinação não é uma das conhecidas). */
  readText: string | null;
  source: CharterProvenance;
}

/** Aprovação de sócios exigida por cláusula de crédito (quórum). */
export interface CharterSigningQuorum {
  text: string;
  source: CharterProvenance;
}

export interface CharterSigningSituation {
  key: CharterSigningSituationKey;
  title: string;
  icon: string;
  lines: CharterSigningLine[];
  /** Sem regra própria: o contrato não especifica, vale a regra geral. */
  usesGeneral: boolean;
  quorum: CharterSigningQuorum[];
}

export interface CharterSigningSummary {
  /** O contrato não diferencia por situação: uma regra geral para todos os atos. */
  singleRule: boolean;
  situations: CharterSigningSituation[];
  attorneys: { text: string; source: CharterProvenance } | null;
  /** Nenhuma regra de assinatura lida. */
  empty: boolean;
}

/** Como a regra se aplica a uma pessoa (coluna "Assina" do quadro de sócios). */
export interface CharterPersonSigning {
  label: string;
  tone: 'sole' | 'joint' | 'approval' | 'none' | 'unknown';
  /** Regras que citam a pessoa, uma por linha, com o ato e a página. */
  tooltip: string | null;
}

interface SigningContext {
  administrators: CharterAdministratorDto[];
  isCorporation: boolean;
  currency: string;
}

export function buildSigningSummary(
  governance: CharterGovernanceDto,
  clauses: CharterClauseDto[],
  isCorporation: boolean,
  currency: string
): CharterSigningSummary {
  const ctx: SigningContext = {
    administrators: governance.administrators,
    isCorporation,
    currency,
  };
  const rules = governance.signingRules;
  const specific = rules.filter(rule => rule.scope !== 'general' && rule.scope !== 'other');
  const attorneys = describeAttorneys(governance.attorneyRule, governance.attorneysAllowed);

  if (rules.length === 0) {
    return { singleRule: false, situations: [], attorneys, empty: true };
  }

  const singleRule = specific.length === 0;
  const situations = SITUATIONS.filter(s => !singleRule || s.key === 'general').map(situation => {
    const own = rules.filter(rule => situation.scopes.includes(rule.scope));
    return {
      key: situation.key,
      title: singleRule ? 'Vale para todos os atos' : situation.title,
      icon: singleRule ? 'pi-check-square' : situation.icon,
      lines: own.map(rule => describeRule(rule, ctx)),
      usesGeneral: own.length === 0,
      quorum: singleRule ? [] : quorumFor(situation.clauses, clauses),
    };
  });
  return { singleRule, situations, attorneys, empty: false };
}

/** Coluna "Assina" do quadro de sócios: como a pessoa assina pela empresa. */
export function personSigning(
  personId: string,
  name: string,
  governance: CharterGovernanceDto,
  isCorporation: boolean,
  currency: string
): CharterPersonSigning {
  const admin = governance.administrators.find(a => a.personId === personId) ?? null;
  const rules = governance.signingRules.filter(rule =>
    rule.signers.length === 0 ? !!admin : rule.signers.some(s => sameName(s, name))
  );
  const ctx: SigningContext = {
    administrators: governance.administrators,
    isCorporation,
    currency,
  };
  if (rules.length === 0) {
    return admin
      ? {
          label: 'Administrador · regra não lida',
          tone: 'unknown',
          tooltip: 'O contrato lido não traz a regra de assinatura.',
        }
      : { label: 'Não assina', tone: 'none', tooltip: null };
  }
  const main =
    rules.find(r => r.scope === 'general') ??
    rules.find(r => r.scope === 'financial-obligations' || r.scope === 'banking') ??
    rules[0];
  const { label, tone } = personLabel(main, name, ctx);
  const tooltip = rules
    .map(rule => {
      const line = describeRule(rule, ctx);
      const where = [rule.actLabel, rule.page ? `p. ${rule.page}` : null]
        .filter(Boolean)
        .join(', ');
      return `${scopeTitle(rule.scope)}: ${line.combination}${line.limit ? ` (${line.limit})` : ''}${where ? ` [${where}]` : ''}`;
    })
    .join('\n');
  return { label: admin ? `${adminNoun(admin, isCorporation)} · ${label}` : label, tone, tooltip };
}

function personLabel(
  rule: CharterSigningRuleDto,
  name: string,
  ctx: SigningContext
): { label: string; tone: CharterPersonSigning['tone'] } {
  switch (rule.mode) {
    case 'sole':
      return { label: 'assina isolado', tone: 'sole' };
    case 'sole-up-to-limit':
      return {
        label: `isolado até ${formatMoney(rule.limitAmount, ctx.currency)}`,
        tone: 'sole',
      };
    case 'joint-any-two':
      return { label: 'em conjunto com 1', tone: 'joint' };
    case 'joint-specific': {
      const others = rule.signers.filter(s => !sameName(s, name));
      return {
        label: others.length === 1 ? `só com ${others[0]}` : 'só com outro administrador',
        tone: 'joint',
      };
    }
    case 'board-approval':
      return { label: 'com aprovação do conselho', tone: 'approval' };
    case 'shareholders-approval':
      return {
        label: `com aprovação dos ${ctx.isCorporation ? 'acionistas' : 'sócios'}`,
        tone: 'approval',
      };
    default:
      return { label: 'ver regra', tone: 'unknown' };
  }
}

function describeRule(rule: CharterSigningRuleDto, ctx: SigningContext): CharterSigningLine {
  const people = signersOf(rule, ctx);
  const many = people.length > 1;
  let combination: string;
  let readText: string | null = null;
  let limit: string | null =
    rule.limitAmount != null
      ? `limite citado: ${formatMoney(rule.limitAmount, ctx.currency)}`
      : null;
  switch (rule.mode) {
    case 'sole':
      combination = many ? 'qualquer um, isoladamente' : 'isoladamente';
      break;
    case 'sole-up-to-limit':
      combination = many ? 'qualquer um, isoladamente' : 'isoladamente';
      limit =
        rule.limitAmount != null ? `até ${formatMoney(rule.limitAmount, ctx.currency)}` : null;
      break;
    case 'joint-any-two':
      combination = '2 em conjunto';
      break;
    case 'joint-specific':
      combination =
        rule.signers.length === 2
          ? 'os dois em conjunto'
          : `${rule.signers.join(' + ')} em conjunto`;
      break;
    case 'board-approval':
      combination = 'precisa de aprovação do Conselho de Administração';
      break;
    case 'shareholders-approval':
      combination = `precisa de aprovação dos ${ctx.isCorporation ? 'acionistas' : 'sócios'}`;
      break;
    default:
      combination = 'regra própria do contrato';
      readText = rule.summary;
  }
  const approval = rule.mode === 'board-approval' || rule.mode === 'shareholders-approval';
  return {
    people: approval ? [] : people,
    combination,
    limit,
    readText,
    source: rule,
  };
}

/** Quem a regra nomeia; sem nomes, os administradores (na S.A., a diretoria, que representa a companhia). */
function signersOf(rule: CharterSigningRuleDto, ctx: SigningContext): CharterSigningPerson[] {
  if (rule.signers.length > 0) {
    return rule.signers.map(name => {
      const admin = ctx.administrators.find(a => sameName(a.name, name));
      return { name, role: admin ? roleOf(admin, ctx.isCorporation) : null };
    });
  }
  const pool = ctx.isCorporation
    ? ctx.administrators.filter(a => a.body === 'executive-board')
    : ctx.administrators.filter(a => a.body === 'administration');
  const list = pool.length > 0 ? pool : ctx.administrators;
  return list.map(admin => ({ name: admin.name, role: roleOf(admin, ctx.isCorporation) }));
}

function roleOf(admin: CharterAdministratorDto, isCorporation: boolean): string {
  return admin.role || (isCorporation ? GOVERNANCE_BODY_LABEL[admin.body] : 'Administrador');
}

function adminNoun(admin: CharterAdministratorDto, isCorporation: boolean): string {
  if (!isCorporation) {
    return 'Administrador';
  }
  return admin.body === 'board-of-directors' ? 'Conselheiro' : 'Diretor';
}

function quorumFor(
  categories: CharterClauseCategory[],
  clauses: CharterClauseDto[]
): CharterSigningQuorum[] {
  return clauses
    .filter(c => categories.includes(c.category) && c.quorumPercent != null)
    .map(c => ({
      text: `Depende de aprovação de sócios com ${formatPercent(c.quorumPercent)} do capital (cláusula de crédito).`,
      source: c,
    }));
}

function describeAttorneys(
  rule: CharterValueDto<string>,
  allowed: CharterValueDto<boolean>
): { text: string; source: CharterProvenance } | null {
  if (rule.value) {
    return { text: rule.value, source: rule };
  }
  if (allowed.value != null) {
    return {
      text: allowed.value
        ? 'Pode nomear procuradores (o contrato não detalha a regra).'
        : 'O contrato não prevê procuradores.',
      source: allowed,
    };
  }
  return null;
}

const SCOPE_TITLE: Record<CharterSigningScope, string> = {
  general: 'Atos do dia a dia',
  'financial-obligations': 'Empréstimos e financiamentos',
  guarantees: 'Garantias',
  'asset-encumbrance': 'Oneração de bens',
  'asset-sale': 'Venda de bens',
  banking: 'Bancos',
  other: 'Outros atos',
};

function scopeTitle(scope: CharterSigningScope): string {
  return SCOPE_TITLE[scope];
}

function sameName(a: string, b: string): boolean {
  const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase();
  return norm(a) === norm(b);
}
